import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { LanguageProvider } from "@/i18n/LanguageContext";
import { ScrollToTop } from "@/components/ScrollToTop";
import Landing from "@/pages/Landing";
import Results from "@/pages/Results";
import Checkout from "@/pages/Checkout";
import Confirmation from "@/pages/Confirmation";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import MyBookings from "@/pages/MyBookings";
import BookingDetail from "@/pages/BookingDetail";
import DriverPortal from "@/pages/DriverPortal";
import AdminPanel from "@/pages/AdminPanel";
import AdminLive from "@/pages/AdminLive";
import AdminForecast from "@/pages/AdminForecast";
import AdminCalendar from "@/pages/AdminCalendar";
import AdminDriverAvailability from "@/pages/AdminDriverAvailability";

const ADMIN_ROLES = ["admin"];
import TripLive from "@/pages/TripLive";
import Support from "@/pages/Support";
import Account from "@/pages/Account";
import Privacy from "@/pages/Privacy";
import Terms from "@/pages/Terms";
import DeleteAccount from "@/pages/DeleteAccount";
import ForgotPassword from "@/pages/ForgotPassword";
import AgencyDashboard from "@/pages/AgencyDashboard";

const Protected = ({ children, roles }) => {
  const { user, loading } = useAuth();
  if (loading)
    return <div className="p-20 text-center text-slate-500">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role))
    return <Navigate to="/bookings" replace />;
  return children;
};

function App() {
  return (
    <div className="App">
      <LanguageProvider>
        <AuthProvider>
          <BrowserRouter>
            <ScrollToTop />
            <Routes>
              <Route path="/" element={<Landing />} />
              <Route path="/results" element={<Results />} />
              <Route path="/checkout" element={<Checkout />} />
              <Route path="/confirmation/:code" element={<Confirmation />} />
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route
                path="/bookings"
                element={
                  <Protected>
                    <MyBookings />
                  </Protected>
                }
              />
              <Route
                path="/bookings/:id"
                element={
                  <Protected>
                    <BookingDetail />
                  </Protected>
                }
              />
              <Route
                path="/driver"
                element={
                  <Protected roles={["vendor", "admin"]}>
                    <DriverPortal />
                  </Protected>
                }
              />
              <Route
                path="/admin"
                element={
                  <Protected>
                    <AdminPanel />
                  </Protected>
                }
              />
              <Route
                path="/admin/live"
                element={
                  <Protected roles={ADMIN_ROLES}>
                    <AdminLive />
                  </Protected>
                }
              />
              <Route
                path="/admin/forecast"
                element={
                  <Protected roles={ADMIN_ROLES}>
                    <AdminForecast />
                  </Protected>
                }
              />
              <Route
                path="/admin/calendar"
                element={
                  <Protected roles={ADMIN_ROLES}>
                    <AdminCalendar />
                  </Protected>
                }
              />
              <Route
                path="/admin/driver-availability"
                element={
                  <Protected roles={ADMIN_ROLES}>
                    <AdminDriverAvailability />
                  </Protected>
                }
              />
              <Route
                path="/trip/:code"
                element={
                  <Protected>
                    <TripLive />
                  </Protected>
                }
              />
              <Route
                path="/support"
                element={
                  <Protected>
                    <Support />
                  </Protected>
                }
              />
              <Route
                path="/account"
                element={
                  <Protected>
                    <Account />
                  </Protected>
                }
              />
              <Route
                path="/agency"
                element={
                  <Protected roles={["agency"]}>
                    <AgencyDashboard />
                  </Protected>
                }
              />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/delete-account" element={<DeleteAccount />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </BrowserRouter>
          <Toaster position="top-right" richColors />
        </AuthProvider>
      </LanguageProvider>
    </div>
  );
}

export default App;
